<template>
  <v-card width="100%" class="summary light-border light-border-override" flat>
    <v-layout wrap>
      <v-flex xs12 sm12 md7 lg7 class="pa-4">
        <v-layout wrap align-center>
          <v-flex xs2 sm1 class="card-icon">
            <v-avatar size="50px" class="avatar">
              <v-img
                v-if="profileImage && profileImage.imageURL"
                :key="user.dateCreated"
                :src="`${profileImage.imageURL}?w=50&h=50&fit=crop`"
                alt="Profile Image"
                :lazy-src="lazyProfileImage"
              />
            </v-avatar>
          </v-flex>
          <v-flex xs10 sm11 v-if="user">
            <router-link
              :to="{
                name: 'userProfile',
                params: {
                  routeReloadRound: true,
                  userID: this.$route.params.userID,
                  id: this.$route.params.userID,
                },
              }"
            >
              <h2 :class="[$vuetify.theme.dark ? 'white--text' : 'black--text', 'player text-h5']">
                {{ user.displayName }}
              </h2>
            </router-link>
          </v-flex>

          <v-flex xs2 sm1 class="card-icon">
            <v-avatar size="50px">
              <v-icon>golf_course</v-icon>
            </v-avatar>
          </v-flex>
          <v-flex xs10 sm11 style="cursor: default">
            <div class="text-h6 font-weight-bold">
              {{ details.gameMode }}

              <router-link
                v-if="eventRound || eventRoundIneligible"
                :to="{
                  name: 'eventDetails',
                  params: {
                    eventID: eventRound || eventRoundIneligible,
                  },
                }"
                style="vertical-align: top"
              >
                <span class="icon-indicator-wrapper">
                  <v-tooltip top v-if="eventRound || eventRoundIneligible">
                    <template v-slot:activator="{ on }">
                      <v-avatar v-on="on" size="18" color="primary" class="icon-indicator">
                        <span class="white--text text-body-2">E</span>
                      </v-avatar>
                    </template>
                    <span>{{ eventName }} - {{ $t('roundDetails.event') }}</span>
                  </v-tooltip>
                </span>
                <span v-if="eventRoundIneligible">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-avatar v-on="on" size="18" color="info" class="icon-indicator">
                        <span class="white--text text-body-2">I</span>
                      </v-avatar>
                    </template>
                    <span>{{ $t('roundDetails.ineligiblePlayer') }}</span>
                  </v-tooltip>
                </span>
              </router-link>
              <span v-if="HCPRound" class="icon-indicator-wrapper">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-avatar v-on="on" size="18" color="success" class="icon-indicator">
                      <span class="white--text text-body-2">H</span>
                    </v-avatar>
                  </template>
                  <span>Round Qualified for Handicap</span>
                </v-tooltip>
              </span>
            </div>
            <div class="created">{{ details.createdAt | utility_formatDateMY }}</div>
          </v-flex>

          <v-flex xs12 offset-xs1 sm8 offset-sm1>
            <div class="text-h6 text-center text-sm-left" v-if="courseName">{{ courseName }}</div>
          </v-flex>

          <v-flex xs12 sm3 class="text-right">
            <v-layout wrap justify-space-around v-if="hasScorecard && showScorecard">
              <v-flex xs6 class="text-center right-border py-0">
                <v-flex class="round-stat-header py-0">Holes</v-flex>
                <transition name="fade" appear>
                  <v-flex class="round-stat user-handicap py-0">
                    {{ holeCount ? holeCount : '&dot;&dot;&dot;' }}
                  </v-flex>
                </transition>
              </v-flex>
              <v-flex xs6 class="text-center py-0">
                <v-flex class="round-stat-header py-0">Par</v-flex>
                <transition name="fade" appear>
                  <v-flex class="round-stat py-0">
                    {{ holeParCount ? holeParCount : '&dot;&dot;&dot;' }}
                  </v-flex>
                </transition>
              </v-flex>
            </v-layout>
            <v-layout
              wrap
              justify-space-around
              v-else-if="!hasScorecard && score !== null && !showScorecard && !isPracticeRange"
            >
              <v-flex xs6 class="text-center py-0">
                <v-flex class="round-stat-header py-0">Score{{ LDCheck ? '(yds)' : '' }}</v-flex>
                <transition name="fade" appear>
                  <v-flex class="round-stat py-0" v-if="KPCheck">
                    {{ score ? utility_convertFeetInches(score) : '&dot;&dot;&dot;' }}
                  </v-flex>
                  <v-flex class="round-stat py-0" v-else-if="LDCheck">
                    {{ score ? utility_roundNumYards(score) : '&dot;&dot;&dot;' }}
                  </v-flex>
                  <v-flex class="round-stat py-0" v-else>
                    {{ score ? score : '&dot;&dot;&dot;' }}
                  </v-flex>
                </transition>
              </v-flex>
            </v-layout>
          </v-flex>

          <v-flex xs11 offset-xs1 class="py-6">
            <v-divider></v-divider>
          </v-flex>

          <v-flex xs11 offset-xs1 class="py- round-data">
            <v-layout wrap align-start justify-center fill-height>
              <v-flex xs12 text-sm-left sm4>
                <div v-if="details"><span>Players:</span> {{ details.players }}</div>
                <div v-if="details"><span>Tee Level:</span> {{ details.teeLevel }}</div>
                <div v-if="details"><span>Pin Position:</span> {{ details.pinPosition }}</div>
                <div v-if="details">
                  <span>AutoCaddie:</span> {{ details.autoCaddie | utility_yesNo }}
                </div>
                <div v-if="details && team"><span>Team:</span> {{ team }}</div>
              </v-flex>
              <v-flex xs12 text-sm-left sm4>
                <div v-if="roundHCP">
                  <span>Round Handicap:</span>
                  {{
                    roundHCP
                      | utility_toFixedOneDecimal
                      | utility_handicapPresence
                      | utility_handicapFormat
                  }}
                </div>
                <div v-if="details"><span>Putting Mode:</span> {{ details.puttingMode }}</div>
                <div v-if="details">
                  <span>Mulligan Setting:</span> {{ details.mulliganSetting }}
                </div>
              </v-flex>
              <v-flex xs12 text-sm-left sm4>
                <div v-if="details"><span>Elevation:</span> {{ details.elevation }}</div>
                <div v-if="details"><span>Stimp Rating:</span> {{ details.stimpRating }}</div>
                <div v-if="details"><span>Green Hardness:</span> {{ details.greenHardness }}</div>
                <div v-if="details"><span>Wind:</span> {{ details.wind }}</div>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-flex>

      <v-flex xs12 sm12 md5 lg5 class="pa-0 course-image" ref="courseImage">
        <v-card
          tile
          width="100%"
          flat
          darken-2
          class="white--text course-image-card event-course-card pa-0"
        >
          <v-img
            :src="courseImg"
            :aspect-ratio="aspect"
            alt="Course Image"
            class="course-image pa-0"
          />
        </v-card>
      </v-flex>
    </v-layout>
  </v-card>
</template>

<script>
import utilityMixin from '@/mixins/utilityMixin';

export default {
  name: 'CourseCard',
  props: {
    details: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
    },
    scorecard: {
      type: Object,
    },
    score: {
      type: Number,
      default: null,
    },
    isStrokePlay: {
      type: Boolean,
      default: false,
    },
    isScramble: {
      type: Boolean,
      default: false,
    },
    showScorecard: {
      type: Boolean,
      default: false,
    },
    presentedBy: {
      type: String,
    },
    gameMode: {
      type: String,
    },
  },

  mixins: [utilityMixin],

  data() {
    return {
      time: new Date(),
      viewWidth: document.body.clientWidth,
      viewHeight: document.body.clientHeight,
      aspect: 1.31,
      loaderColor: '#BA2127',
      loaderSize: '.5em',
      lazyProfileImage:
        'https://e6golf-qa-assets.imgix.net/shared/player-art/chipped.jpg?h=200&w=200&fit=crop',
    };
  },

  mounted() {
    if (this.$refs.courseImage) {
      this.aspect = this.$refs.courseImage.clientWidth / this.$refs.courseImage.clientHeight;
    }
    this.resizeHandler();
    this.$nextTick(() => {
      window.addEventListener('resize', this.resizeHandler);
      this.$on('hook:beforeDestroy', () => {
        window.removeEventListener('resize', this.resizeHandler);
      });
    });
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler);
  },

  computed: {
    eventRound() {
      if (this.utility_isObject(this.details)) {
        return this.details?.eventID && this.details?.eventEligible ? this.details?.eventID : null;
      }
    },

    eventRoundIneligible() {
      if (this.utility_isObject(this.details)) {
        return this.details?.eventID && !this.details?.eventEligible ? this.details?.eventID : null;
      }
    },

    HCPRound() {
      if (this.utility_isObject(this.details)) {
        return this.details?.qualifiedForHandicap ?? false;
      }
    },

    courseName() {
      if (this.details) {
        return this.details?.course?.name;
      }
    },

    holeCount() {
      if (this.details?.holeCountSelected) {
        return this.details.holeCountSelected;
      }

      if (this.scorecard?.holeScores) {
        return this.scorecard?.holeScores.reduce((acc, curr) => {
          if (curr) {
            acc += 1;
          }
          return acc;
        }, 0);
      }
    },

    holeParCount() {
      if (this.scorecard?.holePars?.length && this.details?.selectedHoles?.length) {
        const pars = this.details.selectedHoles.reduce((acc, curr) => {
          const holePar = this.scorecard?.holePars?.[curr];
          if (holePar) {
            acc += holePar;
          }
          return acc;
        }, 0);

        return pars;

        // const holeIndexesWithScores = [];
        // this.scorecard?.holeScores.forEach((item, index) => {
        //   if (item !== null) {
        //     holeIndexesWithScores.push(index);
        //   } else {
        //     holeIndexesWithScores.push(null);
        //   }
        // });

        // return this.scorecard?.holePars.reduce((acc, curr, idx, src) => {
        //   if (idx === holeIndexesWithScores[idx]) {
        //     acc += curr;
        //   }
        //   return acc;
        // }, 0);
      }
    },

    courseImg() {
      if (this.details?.course?.imageURL) {
        return `${this.details.course.imageURL}?fit=crop`;
      }
    },

    profileImage() {
      return this.user?.profileImage ? this.user?.profileImage : '';
    },

    hasScorecard() {
      if (this.scorecard) {
        return Object.keys(this.scorecard).length > 0 && this.scorecard.constructor === Object;
      }
    },

    KPCheck() {
      if (this.gameMode) {
        return this.gameMode === 'closest_to_pin';
      }
      return false;
    },

    LDCheck() {
      if (this.gameMode) {
        return this.gameMode === 'longest_drive';
      }
      return false;
    },

    isPracticeRange() {
      if (this.gameMode) {
        return this.gameMode.includes('practice');
      }
      return false;
    },

    team() {
      if (this.details?.team >= 0) {
        return this.details?.team + 1;
      }
    },

    eventName() {
      if (this.utility_isObject(this.details)) {
        return this.details?.event?.name ?? '';
      }
    },

    roundHCP() {
      if (this.utility_isObject(this.details)) {
        return this.details?.roundHandicap ?? this.user?.handicap;
      }
    },
  },

  methods: {
    resizeHandler() {
      if (this.$refs.courseImage) {
        this.aspect = this.$refs.courseImage.clientWidth / this.$refs.courseImage.clientHeight;
      }
      this.viewWidth = window.innerWidth;
      this.viewHeight = window.innerHeight;
    },
  },
};
</script>

<style scoped>
img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.container {
  min-height: calc(10vh - 65px) !important;
}

.summary {
  border-left: 10px solid var(--primary, #ba2127) !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  overflow: hidden;
}

.light-border-override {
  border-bottom: none !important;
  border-bottom-right-radius: 0px;
  border-bottom-left-radius: 0px;
}

.course-image {
  max-height: 385px;
  min-height: 350px;
}

.card-icon-image {
  width: 30px;
  opacity: 0.6;
}

.theme--dark .card-icon-image {
  opacity: 1;
}

.theme--dark .card-icon {
  background-size: 50%;
  background-position: 10px 8px;
  opacity: 0.9;
}

.avatar img {
  object-fit: contain;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.5);
}

.created {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.4);
  font-style: italic;
}

.round-stat-header {
  font-size: 0.875rem;
  color: var(--grey-light, #95989b);
}

.round-stat {
  font-size: 1.313rem;
}

.right-border {
  border-right: 1px solid var(--grey-lightest, #dddddd);
}

.round-data {
  font-size: 0.9375rem;
  min-height: 100px;
}

.round-data span {
  color: rgba(0, 0, 0, 0.54);
}

.theme--dark .created,
.theme--dark .round-stat-header,
.theme--dark .round-data span {
  color: rgba(255, 255, 255, 0.8);
  color: var(--grey-lightest);
}

.icon-indicator-wrapper {
  display: inline-block;
  max-width: 18px !important;
  max-height: 18px !important;
  vertical-align: top;
}

.icon-indicator {
  max-width: 18px !important;
  max-height: 18px !important;
}

@media (max-width: 1280px) and (min-width: 960px) {
  .avatar {
    height: 40px !important;
    width: 40px !important;
    min-width: 40px !important;
  }
}

@media (max-width: 1024px) {
  .course-image {
    max-height: 40vh;
  }
}

@media (max-width: 600px) {
  .course-image {
    max-height: 20vh;
    min-height: 100px;
  }
  .avatar {
    height: 40px !important;
    width: 40px !important;
    min-width: 40px !important;
  }
}
</style>
